import { useFeatureTernary } from "@viagogo/feature";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useHasRecentNativeAppSession } from "./useNativeAppSession";

export interface AppInstallInterstitialVariant {
  showInterstitial: boolean;
  loading: boolean;
}

/**
 * Resolves exp 103366 (TMHomeSuppressAppInstallInterstitialForNativeUsers).
 * Control → show immediately, no UPS call.
 * Variant → fetch UPS; suppress interstitial if user has a recent native app session.
 * Fail-open: shows interstitial on any API error.
 *
 * @param enabled - pass `appContext.isUserLoggedIn && !appContext.isNativeApp && isStubhub`
 */
export const useAppInstallInterstitialVariant = (enabled: boolean): AppInstallInterstitialVariant => {
  // canParticipate mirrors the enabled predicate so participation is only recorded
  // for users who can actually see the interstitial (logged-in, non-native, StubHub).
  const execVariant = useFeatureTernary(
    FeatureSetEnum.TMHomeSuppressAppInstallInterstitialForNativeUsers,
    () => enabled,
  );
  const isVariant = execVariant({ control: () => false, variant: () => true }) ?? false;

  const { hasRecentNativeAppSession, isLoading } = useHasRecentNativeAppSession(enabled && isVariant);

  if (!isVariant) {
    return { showInterstitial: true, loading: false };
  }

  if (isLoading) {
    // Hide while the UPS fetch is in-flight to avoid a show→suppress flash for native users.
    // Trade-off: variant users without a native session see a brief delay before the interstitial
    // appears. This is preferred over showing then hiding for native users, and UPS latency
    // is low enough to make the delay imperceptible in practice.
    return { showInterstitial: false, loading: true };
  }

  return { showInterstitial: !hasRecentNativeAppSession, loading: false };
};
